import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { EDITOR_LAYER_SELECTOR, type InlineEditorApi } from './inlineEditing';

interface Props<T> {
  display: ReactNode;
  /** Render the autofocused control; call `commit` on Enter/blur, `cancel` on Esc. */
  editor: (api: InlineEditorApi<T>) => ReactNode;
  onSave: (value: T) => Promise<void>;
  /** Bump to open the editor from outside (next-step card "Wpisz wartość"). */
  openRequest?: number;
  /** Applied to the clickable display wrapper. */
  displayClassName?: string;
}

const FLASH_MS = 600;

/**
 * Click-to-edit row: pencil on hover, control on click, save on commit,
 * 600 ms green flash on success, revert + toast on failure. The value shape is
 * the caller's business — this only owns the editing/saving/flash states.
 */
export function InlineField<T>({ display, editor, onSave, openRequest, displayClassName }: Props<T>) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const refocusPending = useRef(false);

  useEffect(() => {
    if (openRequest) setEditing(true);
  }, [openRequest]);

  // Leaving the editor (commit — success or failure — or cancel) unmounts the
  // control that held focus and the browser drops focus on <body>; a keyboard
  // user would have to Tab from the top of the sheet again. So: remember
  // whether focus was still inside the editor when it was left (Enter, Esc,
  // a pick in the popover) — a blur-commit caused by clicking into another
  // field is NOT (focus is already gone by then) and must not steal that
  // field's focus. Captured when commit/cancel is CALLED (the control is
  // disabled while saving, which already blurs it). One frame later, so
  // Radix's own popover/select return-focus (aimed at a detached trigger)
  // has settled, and only when focus really was dropped.
  const focusIsInside = () => {
    const active = document.activeElement;
    return !!active && (!!rootRef.current?.contains(active) || !!active.closest(EDITOR_LAYER_SELECTOR));
  };
  const leaveEditing = useCallback((focusWasInside: boolean) => {
    refocusPending.current = focusWasInside;
    setEditing(false);
  }, []);

  useEffect(() => {
    if (editing || !refocusPending.current) return;
    refocusPending.current = false;
    requestAnimationFrame(() => {
      if (document.activeElement && document.activeElement !== document.body) return;
      buttonRef.current?.focus();
    });
  }, [editing]);

  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  const commit = useCallback(
    async (value: T) => {
      const focusWasInside = focusIsInside();
      setSaving(true);
      try {
        await onSave(value);
        leaveEditing(focusWasInside);
        setFlash(true);
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlash(false), FLASH_MS);
      } catch {
        leaveEditing(focusWasInside);
        toast.error(t('errors.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [onSave, leaveEditing],
  );

  const cancel = useCallback(() => leaveEditing(focusIsInside()), [leaveEditing]);

  if (editing) {
    return (
      <div ref={rootRef} className="flex items-center gap-2">
        {editor({ commit: (value) => void commit(value), cancel, saving, display })}
        {saving ? <Loader2 className="text-muted-foreground size-3.5 shrink-0 animate-spin" /> : null}
      </div>
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'group/inline -mx-1 flex min-h-7 w-full items-center gap-1.5 rounded px-1 text-left transition-colors motion-reduce:transition-none',
        'hover:bg-muted/60 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        flash && 'bg-emerald-500/10',
      )}
    >
      <span className={cn('min-w-0 flex-1', displayClassName)}>{display}</span>
      <Pencil className="text-muted-foreground size-3 shrink-0 opacity-0 transition-opacity group-hover/inline:opacity-100 group-focus-visible/inline:opacity-100 motion-reduce:transition-none" />
    </button>
  );
}
